import { useTranslate } from '../../application/i18n/LocaleProvider';
import useCountUp from '../../application/motion/useCountUp';
import { formatSpecNumber } from '../../domain/cars/carProperties';

/**
 * One instrument on the hero plate, and the reason it is its own file
 * (SPEC-004 D-61).
 *
 * THE FIGURE ROLLS TO THE NEXT CAR'S rather than cutting to it. The plate is
 * four numbers a reader is holding in their head while they swipe — 324 against
 * 295, 1,100 against 1,450 — and a number that is simply DIFFERENT on the next
 * card has to be re-read to be compared. A number that travels is one the eye
 * follows: the direction and the size of the change are felt before either
 * figure has been read.
 *
 * It is a component rather than four lines inside `CarSpecPlate`'s `map`,
 * because `useCountUp` is a hook and a hook cannot be called in a loop. That is
 * the whole reason this file exists; there is no second one.
 *
 * IT ROLLS OVER THE SCALED INTEGER. `useCountUp` rounds — it drives digits, and
 * digits are whole — so a 0-100 time rolled directly would count 4.1 as 4 and
 * land on the tenth only in its final frame. Multiplying by the figure's own
 * precision first and dividing after is what lets a tenth roll like a unit,
 * without the hook having to learn about decimals.
 *
 * AND IT IS PRINTED BY THE DOMAIN, not by this file. `formatSpecNumber` is what
 * knows that 1,100 is 1.100 in German; a component that assembled the string
 * itself would be a component that had quietly decided the grouping. The rolling
 * is the only thing here.
 */
export default function CarSpecPlateCell({ spec, languageCode }) {
    const t = useTranslate();
    const scale = 10 ** spec.fractionDigits;
    // Null all the way through for a figure the catalogue does not have: the
    // hook answers its input unchanged, and the em dash the domain already
    // printed is what gets drawn. An em dash that counted up would be a hole
    // pretending to be a measurement.
    const rolled = useCountUp(spec.amount === null ? null : Math.round(spec.amount * scale));
    const figure = spec.amount === null
        ? spec.value
        : formatSpecNumber(rolled / scale, { languageCode, fractionDigits: spec.fractionDigits });

    return (
        <div className="spec-plate__cell">
            <dt className="spec-plate__label">{t(spec.shortLabelKey)}</dt>
            <dd className="spec-plate__value">
                <span className="spec-plate__figure figure">{figure}</span>
                {spec.unitKey ? <span className="spec-plate__unit">{t(spec.unitKey)}</span> : null}
            </dd>
        </div>
    );
}
